import { db, storage } from '../firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    COLLECTION_EVENTS,
    SOURCE_SYSTEM,
    SCHEMA_VERSION,
    EVENT_STATUS_DRAFT
} from '../shared/constants';

/**
 * Create a new event
 */
export async function createEvent(eventData, bannerFile = null) {
    try {
        let bannerUrl = null;

        // Upload banner if provided
        if (bannerFile) {
            const storageRef = ref(storage, `events/${Date.now()}_${bannerFile.name}`);
            await uploadBytes(storageRef, bannerFile);
            bannerUrl = await getDownloadURL(storageRef);
        }

        const event = {
            ...eventData,
            bannerUrl,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            sourceSystem: SOURCE_SYSTEM,
            schemaVersion: SCHEMA_VERSION,
        };

        const docRef = await addDoc(collection(db, COLLECTION_EVENTS), event);
        return { id: docRef.id, ...event };
    } catch (error) {
        console.error('Error creating event:', error);
        throw error;
    }
}

/**
 * Update an existing event
 */
export async function updateEvent(eventId, eventData, bannerFile = null) {
    try {
        let bannerUrl = eventData.bannerUrl;

        // Upload new banner if provided
        if (bannerFile) {
            const storageRef = ref(storage, `events/${Date.now()}_${bannerFile.name}`);
            await uploadBytes(storageRef, bannerFile);
            bannerUrl = await getDownloadURL(storageRef);
        }

        const updates = {
            ...eventData,
            bannerUrl,
            updatedAt: Timestamp.now(),
        };

        await updateDoc(doc(db, COLLECTION_EVENTS, eventId), updates);
        return { id: eventId, ...updates };
    } catch (error) {
        console.error('Error updating event:', error);
        throw error;
    }
}

/**
 * Delete an event
 */
export async function deleteEvent(eventId) {
    try {
        await deleteDoc(doc(db, COLLECTION_EVENTS, eventId));
    } catch (error) {
        console.error('Error deleting event:', error);
        throw error;
    }
}

/**
 * Get all events
 */
export async function getAllEvents() {
    try {
        const q = query(collection(db, COLLECTION_EVENTS), orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error fetching events:', error);
        throw error;
    }
}

/**
 * Get event by ID
 */
export async function getEventById(eventId) {
    try {
        const docSnap = await getDoc(doc(db, COLLECTION_EVENTS, eventId));
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error('Error fetching event:', error);
        throw error;
    }
}

/**
 * Get events filtered by guild IDs (for coordinators)
 */
export async function getEventsByGuilds(guildIds) {
    try {
        if (!guildIds || guildIds.length === 0) return [];

        const q = query(
            collection(db, COLLECTION_EVENTS),
            where('targetGuildIds', 'array-contains-any', [...guildIds, 'ALL']),
            orderBy('date', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error fetching events by guilds:', error);
        throw error;
    }
}

/**
 * Publish an event (change status from Draft to Published)
 */
export async function publishEvent(eventId) {
    try {
        await updateDoc(doc(db, COLLECTION_EVENTS, eventId), {
            status: 'Published',
            updatedAt: Timestamp.now(),
        });
    } catch (error) {
        console.error('Error publishing event:', error);
        throw error;
    }
}
